import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CSVUploadSuccessPanel = ({ data, onDismiss, glossaryStatus }) => {
  const [manifestPage, setManifestPage] = useState(0);
  const MANIFEST_PAGE_SIZE = 10;

  // --- Derived stats ---
  const lines = data?.lines ?? [];
  const rawData = data?.rawData ?? [];

  // Character count (total characters in all dialogue text)
  const totalCharCount = lines?.reduce((sum, l) => sum + (l?.dialogue?.length ?? 0), 0);

  // Line type breakdown
  const lineTypeBreakdown = lines?.reduce(
    (acc, l) => {
      const t = l?.lineType ?? 'LINE';
      acc[t] = (acc?.[t] ?? 0) + 1;
      return acc;
    },
    { LINE: 0, CHARACTER: 0, STAGE_DIRECTION: 0 }
  );

  // Validation summary
  const rowsProcessed = rawData?.length;
  const rowsSkipped = rawData?.filter(r => {
    const gid = r?.gid?.toString()?.trim();
    return !gid || gid === '' || isNaN(parseInt(gid, 10));
  })?.length;
  const rowsImported = lines?.length;
  const validationErrors = rowsProcessed - rowsImported - rowsSkipped;

  // Import manifest: per-character line count + act/scene range
  const characterManifest = (() => {
    const map = new Map();
    lines?.forEach(l => {
      if (l?.lineType !== 'LINE' || !l?.character) return;
      const key = l?.character?.toUpperCase()?.trim();
      if (!map?.has(key)) {
        map?.set(key, {
          name: l?.character,
          lineCount: 0,
          minAct: Infinity,
          maxAct: -Infinity,
          minScene: Infinity,
          maxScene: -Infinity,
          scenes: new Set()
        });
      }
      const entry = map?.get(key);
      entry.lineCount++;
      entry.minAct = Math.min(entry?.minAct, l?.actNumber ?? Infinity);
      entry.maxAct = Math.max(entry?.maxAct, l?.actNumber ?? -Infinity);
      entry?.scenes?.add(`${l?.actNumber}.${l?.sceneNumber}`);
    });

    return Array.from(map?.values())?.map(e => ({
        name: e?.name,
        lineCount: e?.lineCount,
        actRange: e?.minAct === e?.maxAct
          ? `Act ${e?.minAct}`
          : `Act ${e?.minAct}–${e?.maxAct}`,
        sceneCount: e?.scenes?.size
      }))?.sort((a, b) => b?.lineCount - a?.lineCount);
  })();

  const totalManifestPages = Math.ceil(characterManifest?.length / MANIFEST_PAGE_SIZE);
  const visibleManifest = characterManifest?.slice(
    manifestPage * MANIFEST_PAGE_SIZE,
    (manifestPage + 1) * MANIFEST_PAGE_SIZE
  );

  const formatNumber = (n) => (n ?? 0)?.toLocaleString();

  return (
    <div className="rounded-xl border border-success/30 bg-success/5 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-success/10 border-b border-success/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
            <Icon name="CheckCircle2" size={22} className="text-success" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-card-foreground">Import Successful</h3>
            <p className="text-xs text-muted-foreground">
              {data?.fileName ?? 'CSV file'} processed and saved
            </p>
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-md hover:bg-success/10 transition-colors"
            aria-label="Dismiss"
          >
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>
      <div className="p-6 space-y-6">
        {/* Top stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            icon="Type"
            label="Characters Extracted"
            value={formatNumber(totalCharCount)}
            sub="total text characters"
            color="primary"
          />
          <StatTile
            icon="BookOpen"
            label="Total Rows"
            value={formatNumber(data?.totalLines ?? lines?.length)}
            sub={`${data?.totalActs ?? '?'} acts · ${data?.totalScenes ?? '?'} scenes`}
            color="accent"
          />
          <StatTile
            icon="Users"
            label="Unique Characters"
            value={formatNumber(data?.characterCount)}
            sub="speaking roles"
            color="success"
          />
          <StatTile
            icon="ShieldCheck"
            label="Rows Imported"
            value={formatNumber(rowsImported)}
            sub={`of ${formatNumber(rowsProcessed)} processed`}
            color="muted"
          />
        </div>

        {/* Line type breakdown */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="BarChart2" size={16} className="text-primary" />
            <h4 className="text-sm font-semibold text-card-foreground">Line Type Breakdown</h4>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <LineTypeBadge
              label="Dialogue Lines"
              count={lineTypeBreakdown?.LINE}
              total={lines?.length}
              color="bg-primary/10 text-primary border-primary/20"
              icon="MessageSquare"
            />
            <LineTypeBadge
              label="Character Entries"
              count={lineTypeBreakdown?.CHARACTER}
              total={lines?.length}
              color="bg-accent/10 text-accent border-accent/20"
              icon="User"
            />
            <LineTypeBadge
              label="Stage Directions"
              count={lineTypeBreakdown?.STAGE_DIRECTION}
              total={lines?.length}
              color="bg-muted text-muted-foreground border-border"
              icon="Film"
            />
          </div>
        </div>

        {/* Validation summary */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="ClipboardCheck" size={16} className="text-success" />
            <h4 className="text-sm font-semibold text-card-foreground">Validation Summary</h4>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <ValidationRow
              icon="ListChecks"
              label="Rows Processed"
              value={formatNumber(rowsProcessed)}
              variant="neutral"
            />
            <ValidationRow
              icon="CheckCircle"
              label="Rows Imported"
              value={formatNumber(rowsImported)}
              variant="success"
            />
            <ValidationRow
              icon={rowsSkipped > 0 ? 'AlertTriangle' : 'MinusCircle'}
              label="Rows Skipped"
              value={formatNumber(rowsSkipped)}
              variant={rowsSkipped > 0 ? 'warning' : 'neutral'}
            />
          </div>
          {validationErrors > 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-error bg-error/5 border border-error/20 rounded-md px-3 py-2">
              <Icon name="AlertCircle" size={13} />
              <span>{formatNumber(validationErrors)} row(s) had validation issues and were excluded from import.</span>
            </div>
          )}
          {rowsSkipped === 0 && validationErrors === 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-success bg-success/5 border border-success/20 rounded-md px-3 py-2">
              <Icon name="CheckCircle" size={13} />
              <span>All rows passed validation — zero errors detected.</span>
            </div>
          )}
        </div>

        {/* Import manifest */}
        {characterManifest?.length > 0 && (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="Table" size={16} className="text-primary" />
                <h4 className="text-sm font-semibold text-card-foreground">Import Manifest</h4>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {characterManifest?.length} characters
                </span>
              </div>
              {totalManifestPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setManifestPage(p => Math.max(0, p - 1))}
                    disabled={manifestPage === 0}
                    className="p-1 rounded hover:bg-muted disabled:opacity-40 transition-colors"
                    aria-label="Previous page"
                  >
                    <Icon name="ChevronLeft" size={14} className="text-muted-foreground" />
                  </button>
                  <span className="text-xs text-muted-foreground px-1">
                    {manifestPage + 1} / {totalManifestPages}
                  </span>
                  <button
                    onClick={() => setManifestPage(p => Math.min(totalManifestPages - 1, p + 1))}
                    disabled={manifestPage === totalManifestPages - 1}
                    className="p-1 rounded hover:bg-muted disabled:opacity-40 transition-colors"
                    aria-label="Next page"
                  >
                    <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">#</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Character</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Lines</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Act Range</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Scenes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visibleManifest?.map((char, idx) => {
                    const globalIdx = manifestPage * MANIFEST_PAGE_SIZE + idx;
                    const isTop = globalIdx < 3;
                    return (
                      <tr
                        key={char?.name}
                        className={`transition-colors ${
                          isTop ? 'bg-primary/3 hover:bg-primary/5' : 'hover:bg-muted/40'
                        }`}
                      >
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">
                          {isTop ? (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 text-primary text-xs font-bold">
                              {globalIdx + 1}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">{globalIdx + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`font-medium ${
                            isTop ? 'text-card-foreground' : 'text-card-foreground/80'
                          }`}>
                            {char?.name}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span className={`font-semibold tabular-nums ${
                            isTop ? 'text-primary' : 'text-card-foreground'
                          }`}>
                            {formatNumber(char?.lineCount)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            {char?.actRange}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs text-muted-foreground tabular-nums">
                          {char?.sceneCount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Glossary auto-population status */}
        {glossaryStatus && (
          <div className={`rounded-lg border p-4 flex items-start gap-3 ${
            glossaryStatus?.status === 'done'
              ? 'border-primary/20 bg-primary/5'
              : glossaryStatus?.status === 'error' ?'border-destructive/20 bg-destructive/5' :'border-border bg-muted/30'
          }`}>
            <div className={`mt-0.5 flex-shrink-0 ${
              glossaryStatus?.status === 'scanning' ? 'animate-spin' : ''
            }`}>
              <Icon
                name={
                  glossaryStatus?.status === 'done'
                    ? 'BookMarked'
                    : glossaryStatus?.status === 'error' ?'AlertCircle' :'Loader2'
                }
                size={18}
                className={
                  glossaryStatus?.status === 'done'
                    ? 'text-primary'
                    : glossaryStatus?.status === 'error' ?'text-destructive' :'text-muted-foreground'
                }
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-card-foreground">
                {glossaryStatus?.status === 'done'
                  ? 'Glossary Auto-Population Complete'
                  : glossaryStatus?.status === 'error' ?'Glossary Scan Issue' :'Scanning for Archaic Words...'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{glossaryStatus?.message}</p>
              {glossaryStatus?.status === 'done' && glossaryStatus?.added > 0 && (
                <div className="flex items-center gap-3 mt-2">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    <Icon name="Plus" size={11} />
                    {glossaryStatus?.added} new term{glossaryStatus?.added !== 1 ? 's' : ''} added
                  </span>
                  {glossaryStatus?.skipped > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      <Icon name="SkipForward" size={11} />
                      {glossaryStatus?.skipped} already existed
                    </span>
                  )}
                </div>
              )}
              {glossaryStatus?.status === 'done' && glossaryStatus?.added > 0 && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-md px-2.5 py-1.5">
                  <Icon name="Clock" size={12} className="flex-shrink-0" />
                  <span>Definitions are <strong>AI-generated</strong> and marked <strong>pending review</strong> — visit Glossary Management to approve or edit before publishing.</span>
                </div>
              )}
              {glossaryStatus?.status === 'done' && glossaryStatus?.added === 0 && (
                <p className="text-xs text-muted-foreground mt-1 italic">No new archaic terms discovered beyond existing glossary entries.</p>
              )}
              {glossaryStatus?.status === 'complete' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-success">
                    <Icon name="CheckCircle" size={16} />
                    <span className="text-sm font-medium">
                      {glossaryStatus?.added > 0
                        ? `${glossaryStatus?.added} new archaic term${glossaryStatus?.added !== 1 ? 's' : ''} added to Glossary`
                        : 'No new archaic terms found'}
                    </span>
                  </div>
                  {glossaryStatus?.skipped > 0 && (
                    <p className="text-xs text-muted-foreground ml-6">
                      {glossaryStatus?.skipped} term{glossaryStatus?.skipped !== 1 ? 's' : ''} already existed — skipped
                    </p>
                  )}
                  {glossaryStatus?.added > 0 && (
                    <>
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 ml-0">
                        <Icon name="Sparkles" size={14} />
                        <span className="text-xs font-medium">
                          AI-generated definitions — pending admin review
                        </span>
                      </div>
                      {/* Audio generation results */}
                      {(glossaryStatus?.audioSucceeded > 0 || glossaryStatus?.audioFailed > 0) && (
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                          {glossaryStatus?.audioSucceeded > 0 && (
                            <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                              <Icon name="Volume2" size={13} />
                              <span className="text-xs font-medium">
                                {glossaryStatus?.audioSucceeded} pronunciation audio{glossaryStatus?.audioSucceeded !== 1 ? 's' : ''} generated
                              </span>
                            </div>
                          )}
                          {glossaryStatus?.audioFailed > 0 && (
                            <div className="flex items-center gap-1.5 text-red-500 dark:text-red-400">
                              <Icon name="VolumeX" size={13} />
                              <span className="text-xs font-medium">
                                {glossaryStatus?.audioFailed} audio generation{glossaryStatus?.audioFailed !== 1 ? 's' : ''} failed
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Visit <span className="font-medium text-foreground">Glossary Management</span> to review definitions and approve audio before publishing.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Sub-components ---

const StatTile = ({ icon, label, value, sub, color }) => {
  const colorMap = {
    primary: 'bg-primary/10 text-primary',
    accent: 'bg-accent/10 text-accent',
    success: 'bg-success/10 text-success',
    muted: 'bg-muted text-muted-foreground'
  };
  return (
    <div className="rounded-lg border border-border bg-card p-3.5 flex flex-col gap-2">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${colorMap?.[color] ?? colorMap?.muted}`}>
        <Icon name={icon} size={16} />
      </div>
      <div>
        <p className="text-xl font-bold text-card-foreground tabular-nums leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{sub}</p>
      </div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
    </div>
  );
};

const LineTypeBadge = ({ label, count, total, color, icon }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className={`rounded-lg border p-3 ${color}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon name={icon} size={13} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-lg font-bold tabular-nums">{count?.toLocaleString()}</p>
      <div className="mt-1.5 h-1 rounded-full bg-current/10 overflow-hidden">
        <div
          className="h-full rounded-full bg-current/40 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs mt-1 opacity-70">{pct}% of total</p>
    </div>
  );
};

const ValidationRow = ({ icon, label, value, variant }) => {
  const variantMap = {
    success: 'text-success',
    warning: 'text-warning',
    error: 'text-error',
    neutral: 'text-muted-foreground'
  };
  return (
    <div className="flex flex-col gap-1 p-3 rounded-lg bg-muted/40 border border-border">
      <div className={`flex items-center gap-1.5 ${variantMap?.[variant] ?? variantMap?.neutral}`}>
        <Icon name={icon} size={13} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-lg font-bold text-card-foreground tabular-nums">{value}</p>
    </div>
  );
};

export default CSVUploadSuccessPanel;
